<template>
    <div class="h-full w-full flex flex-col">
        <div class="h-full container mx-auto">
            <div class="px-8 flex flex-col h-full w-full py-8 space-y-8">
                <!-- Breadcrumb -->
                <div class="w-full bg-white rounded-2xl border shadow-lg px-6 md:px-8 py-4 flex flex-col">
                    <div class="flex items-center text-sm font-light">
                        <router-link to="/dashboard" class="opacity-50 hover:opacity-100">Dashboard</router-link>
                        <span class="mx-2">/</span>
                        <router-link to="/my-work-readiness-program" class="opacity-50 hover:opacity-100">Work Readiness
                            Program</router-link>
                        <span class="mx-2">/</span>
                        <span>{{ activity.title || 'Activity' }}</span>
                    </div>
                    <div class="font-bold">{{ activity.title || 'Loading...' }}</div>
                </div>

                <!-- Loading State -->
                <div v-if="isLoading || $apollo.queries.activity.loading"
                    class="h-full flex justify-center items-center">
                    <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
                </div>

                <!-- Error State -->
                <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                    <p class="text-red-600 font-medium">{{ error }}</p>
                    <button @click="$router.push('/my-work-readiness-program')"
                        class="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg">
                        Go Back
                    </button>
                </div>

                <!-- Activity Content -->
                <div v-else>
                    <!-- Activity Card -->
                    <div class="bg-white rounded-2xl shadow-lg p-6 sm:p-8 space-y-4">
                        <div class="flex items-start justify-between">
                            <div class="flex-1">
                                <h1 class="text-2xl font-bold text-indigo-700 mb-2">{{ activity.title }}</h1>
                                <p class="text-gray-600 text-sm leading-relaxed">{{ activity.description }}</p>
                            </div>
                            <div class="ml-4 bg-indigo-100 rounded-lg px-4 py-2 text-center">
                                <div class="text-xs text-indigo-600 font-medium">Points</div>
                                <div class="text-xl font-bold text-indigo-700">{{ activity.points }}</div>
                            </div>
                        </div>
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-600 pt-4 border-t">
                            <div><strong class="text-gray-700">Valuation Type:</strong> {{ activity.valuation_type ||
                                'N/A' }}</div>
                            <div><strong class="text-gray-700">Activity Type:</strong> {{ activity.activity_type ||
                                'N/A' }}</div>
                            <div v-if="activity.start_date"><strong class="text-gray-700">Start Date:</strong> {{
                                formatDate(activity.start_date) }}</div>
                            <div v-if="activity.end_date"><strong class="text-gray-700">End Date:</strong> {{
                                formatDate(activity.end_date) }}</div>
                        </div>
                    </div>

                    <!-- Status Messages -->
                    <!-- Upcoming Activity -->
                    <div v-if="activityStatus === 'upcoming'"
                        class="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 text-center mt-8">
                        <div class="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
                            <svg class="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                        </div>
                        <h3 class="text-lg font-bold text-yellow-800 mb-2">This Activity is Upcoming</h3>
                        <p class="text-yellow-700">This activity hasn't started yet. Please check back after the start
                            date.</p>
                        <p v-if="activity.start_date" class="text-sm text-yellow-600 mt-2">
                            Starts on: <strong>{{ formatDate(activity.start_date) }}</strong>
                        </p>
                    </div>

                    <!-- Past Activity - Always show ended message, and submission details if exists -->
                    <div v-else-if="activityStatus === 'past'">
                        <!-- Always show "This Activity Has Ended" message -->
                        <div class="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 text-center mt-8">
                            <div
                                class="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                                <svg class="w-8 h-8 text-gray-600" fill="none" stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                            </div>
                            <h3 class="text-lg font-bold text-gray-800 mb-2">This Activity Has Ended</h3>
                            <p class="text-gray-700">This activity is no longer accepting submissions.</p>
                            <p v-if="activity.end_date" class="text-sm text-gray-600 mt-2">
                                Ended on: <strong>{{ formatDate(activity.end_date) }}</strong>
                            </p>
                        </div>

                        <!-- If user submitted, show submission details below -->
                        <div v-if="userSubmission"
                            class="bg-green-50 border-2 border-green-200 rounded-xl p-6 sm:p-8 mt-6">
                            <div class="flex items-center mb-4">
                                <div
                                    class="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mr-4">
                                    <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                </div>
                                <div>
                                    <h2 class="text-xl font-bold text-green-800">Your Submission</h2>
                                    <p class="text-sm text-green-600">You submitted this activity when it was active</p>
                                </div>
                            </div>

                            <!-- Submission Details -->
                            <div class="bg-white rounded-lg p-5 space-y-4 border border-green-200">
                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label class="text-xs text-gray-500 font-medium">Submission Date</label>
                                        <p class="text-sm font-semibold text-gray-800">{{
                                            formatDate(userSubmission.created_at) }}</p>
                                    </div>
                                    <div>
                                        <label class="text-xs text-gray-500 font-medium">Status</label>
                                        <p class="text-sm">
                                            <span class="px-3 py-1 rounded-full font-semibold text-xs"
                                                :class="getStatusClass(userSubmission.status)">
                                                {{ formatStatus(userSubmission.status) }}
                                            </span>
                                        </p>
                                    </div>
                                    <div v-if="userSubmission.points">
                                        <label class="text-xs text-gray-500 font-medium">Points Awarded</label>
                                        <p class="text-sm font-bold text-green-600">{{ userSubmission.points }} pts</p>
                                    </div>
                                </div>

                                <div v-if="userSubmission.note">
                                    <label class="text-xs text-gray-500 font-medium mb-1 block">Your Notes</label>
                                    <div class="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">{{
                                        userSubmission.note }}</div>
                                </div>

                                <div v-if="userSubmission.attachments && userSubmission.attachments.length > 0">
                                    <label class="text-xs text-gray-500 font-medium mb-2 block">Uploaded
                                        Documents</label>
                                    <div class="space-y-2">
                                        <a v-for="(attachment, idx) in userSubmission.attachments" :key="idx"
                                            :href="getFileUrl(attachment.path)" target="_blank"
                                            class="flex items-center space-x-2 text-sm text-indigo-600 hover:text-indigo-800 p-2 bg-gray-50 rounded hover:bg-gray-100">
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z">
                                                </path>
                                            </svg>
                                            <span>{{ attachment.file_name || attachment.original_filename || 'Document'
                                            }}</span>
                                        </a>
                                    </div>
                                </div>

                                <div>
                                    <label class="text-xs text-gray-500 font-medium mb-2 block">Conversation with reviewer</label>
                                    <submission-chat
                                        :submission-id="userSubmission.id"
                                        student-name="You"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Current Activity - Show submission if exists, otherwise show form -->
                    <div v-else-if="activityStatus === 'current'">
                        <!-- Already Submitted (internal or external confirmed) -->
                        <div v-if="userSubmission"
                            class="bg-green-50 border-2 border-green-200 rounded-xl p-6 sm:p-8 mt-8">
                            <div class="flex items-center mb-4">
                                <div
                                    class="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mr-4">
                                    <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                </div>
                                <div>
                                    <h2 class="text-xl font-bold text-green-800">Activity Already Submitted</h2>
                                    <p class="text-sm text-green-600">Your submission has been received</p>
                                </div>
                            </div>

                            <!-- Submission Details -->
                            <div class="bg-white rounded-lg p-5 space-y-4 border border-green-200">
                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label class="text-xs text-gray-500 font-medium">Submission Date</label>
                                        <p class="text-sm font-semibold text-gray-800">{{
                                            formatDate(userSubmission.created_at) }}</p>
                                    </div>
                                    <div>
                                        <label class="text-xs text-gray-500 font-medium">Status</label>
                                        <p class="text-sm">
                                            <span class="px-3 py-1 rounded-full font-semibold text-xs"
                                                :class="getStatusClass(userSubmission.status)">
                                                {{ formatStatus(userSubmission.status) }}
                                            </span>
                                        </p>
                                    </div>
                                    <div v-if="userSubmission.points">
                                        <label class="text-xs text-gray-500 font-medium">Points Awarded</label>
                                        <p class="text-sm font-bold text-green-600">{{ userSubmission.points }} pts</p>
                                    </div>
                                </div>

                                <div v-if="userSubmission.note">
                                    <label class="text-xs text-gray-500 font-medium mb-1 block">Your Notes</label>
                                    <div class="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">{{
                                        userSubmission.note }}</div>
                                </div>

                                <div v-if="userSubmission.attachments && userSubmission.attachments.length > 0">
                                    <label class="text-xs text-gray-500 font-medium mb-2 block">Uploaded
                                        Documents</label>
                                    <div class="space-y-2">
                                        <a v-for="(attachment, idx) in userSubmission.attachments" :key="idx"
                                            :href="getFileUrl(attachment.path)" target="_blank"
                                            class="flex items-center space-x-2 text-sm text-indigo-600 hover:text-indigo-800 p-2 bg-gray-50 rounded hover:bg-gray-100">
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z">
                                                </path>
                                            </svg>
                                            <span>{{ attachment.file_name || attachment.original_filename || 'Document'
                                                }}</span>
                                        </a>
                                    </div>
                                </div>

                                <div>
                                    <label class="text-xs text-gray-500 font-medium mb-2 block">Conversation with reviewer</label>
                                    <submission-chat
                                        :submission-id="userSubmission.id"
                                        student-name="You"
                                    />
                                </div>
                            </div>
                        </div>
                        <!-- Submit Form: For internal always; for external only after opening registration link -->
                        <div v-if="!userSubmission && (!isExternalActivity || currentUserHasClickedLink)"
                            class="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mt-8">
                            <h2 class="text-xl font-semibold text-indigo-700 mb-4">Submit Your Activity</h2>
                            <!-- For external: Open Registration Link (shown before form) -->
                            <div v-if="isExternalActivity"
                                class="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <a :href="activity.external_link" target="_blank" rel="noopener noreferrer"
                                    @click.prevent="handleOpenExternalLink"
                                    class="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg px-4 py-2 text-sm">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14">
                                        </path>
                                    </svg>
                                    <span>Open Registration Link</span>
                                </a>
                                <p class="text-xs text-gray-500 mt-2">Opens in new tab. Complete registration there,
                                    then submit below.</p>
                            </div>
                            <form @submit.prevent="handleSubmit" class="space-y-4">
                                <div>
                                    <label class="block text-gray-700 mb-1 font-medium">Your Notes</label>
                                    <textarea v-model="form.note" rows="5"
                                        :placeholder="isExternalActivity ? 'Registered via external link (default)' : 'Write your learning experience...'"
                                        class="w-full border rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"></textarea>
                                </div>
                                <div>
                                    <label class="block text-gray-700 mb-1 font-medium">Upload Document (PDF,
                                        DOCX)</label>
                                    <input ref="fileInput" type="file" @change="handleFileUpload"
                                        accept=".pdf,.doc,.docx"
                                        class="w-full border rounded-lg p-2 text-sm file:bg-indigo-600 file:text-white file:border-0 file:px-4 file:py-2 file:rounded file:cursor-pointer" />
                                    <p v-if="form.file" class="text-xs text-gray-500 mt-1">Selected: {{ form.file.name
                                        }}</p>
                                </div>
                                <div v-if="activity.points" class="p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                                    <p class="text-sm text-indigo-700">
                                        <strong>You will earn {{ activity.points }} point{{ activity.points !== 1 ? 's' : '' }}</strong> upon successful submission.
                                    </p>
                                </div>
                                <div class="text-right">
                                    <button type="submit"
                                        :disabled="isSubmitting || isConfirming || (isExternalActivity && !currentUserHasClickedLink)"
                                        :class="[
                                            'font-semibold rounded-lg px-6 py-3 text-sm shadow-md transition-all',
                                            (isSubmitting || isConfirming || (isExternalActivity && !currentUserHasClickedLink))
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-75'
                                                : 'bg-indigo-600 hover:bg-indigo-700 text-white cursor-pointer'
                                        ]">
                                        <span v-if="isSubmitting || isConfirming">Submitting...</span>
                                        <span v-else-if="isExternalActivity && !currentUserHasClickedLink">Open
                                            Registration Link First</span>
                                        <span v-else-if="isExternalActivity">I've Completed Registration</span>
                                        <span v-else>Submit</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                        <!-- For external: show Open Registration Link only when form is hidden (before they clicked) -->
                        <div v-else-if="isExternalActivity && !userSubmission && !currentUserHasClickedLink"
                            class="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mt-8">
                            <h2 class="text-xl font-semibold text-indigo-700 mb-4">Submit Your Activity</h2>
                            <p v-if="activity.points" class="mb-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100 text-sm text-indigo-700">
                                <strong>Earn {{ activity.points }} point{{ activity.points !== 1 ? 's' : '' }}</strong> by completing this activity.
                            </p>
                            <p class="text-gray-600 mb-4">Open the registration link below first. The submit form will
                                appear after you open it.</p>
                            <a :href="activity.external_link" target="_blank" rel="noopener noreferrer"
                                @click.prevent="handleOpenExternalLink"
                                class="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg px-6 py-3 text-sm shadow-md">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14">
                                    </path>
                                </svg>
                                <span>Open Registration Link</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import gql from 'graphql-tag';
import SubmissionChat from '../../components/work-readiness/SubmissionChat.vue';

const FETCH_ACTIVITY = gql`
  query FetchWorkReadinessProgram($token: String!) {
    fetchWorkReadinessProgram(token: $token) {
      error
      error_code
      message
      current_user_has_clicked_link
      data {
        token
        title
        description
        points
        valuation_type
        activity_type
        is_external
        external_link
        start_date
        end_date
        creator {
          id
          name
        }
      }
    }
  }
`;

const GET_MY_SUBMISSION = gql`
  query GetMySubmission {
    me {
      error
      message
      user {
        workReadinessSubmissions {
          id
          status
          points
          note
          created_at
          updated_at
          activity {
            token
          }
          attachments {
            id
            file_name
            original_filename
            path
          }
        }
      }
    }
  }
`;

const SUBMIT_ACTIVITY_MUTATION = gql`
  mutation SubmitWorkReadinessProgramActivity($token: String!, $input: ActivitySubmissionInput!) {
    submitWorkReadinessProgramActivity(token: $token, input: $input) {
      error
      message
    }
  }
`;

const RECORD_EXTERNAL_LINK_CLICK = gql`
  mutation RecordExternalLinkClick($token: String!) {
    recordExternalLinkClick(token: $token) {
      error
      message
    }
  }
`;

const CONFIRM_EXTERNAL_REGISTRATION = gql`
  mutation ConfirmExternalRegistration($token: String!, $input: ActivitySubmissionInput) {
    confirmExternalRegistration(token: $token, input: $input) {
      error
      message
    }
  }
`;

export default {
    name: "my-work-readiness-program-activity",
    components: {
        SubmissionChat,
    },
    data() {
        return {
            isLoading: false,
            isSubmitting: false,
            isConfirming: false,
            error: null,
            activity: {},
            userSubmission: null,
            currentUserHasClickedLink: false,
            form: {
                note: '',
                file: null,
            },
        };
    },
    props: {
        token: {
            type: String,
            required: true,
        },
    },
    apollo: {
        activity: {
            query: FETCH_ACTIVITY,
            variables() {
                return {
                    token: this.token,
                };
            },
            update(data) {
                if (data.fetchWorkReadinessProgram.error) {
                    this.error = data.fetchWorkReadinessProgram.message || 'Failed to load activity';
                    this.currentUserHasClickedLink = false;
                    return {};
                }
                this.currentUserHasClickedLink = data.fetchWorkReadinessProgram.current_user_has_clicked_link || false;
                return data.fetchWorkReadinessProgram.data || {};
            },
            error(error) {
                this.error = error.message || 'Failed to load activity';
            },
            fetchPolicy: 'network-only',
        },
        mySubmissions: {
            query: GET_MY_SUBMISSION,
            skip() {
                return !this.token;
            },
            update(data) {
                this.findUserSubmission(data);
                return data;
            },
            result({ data }) {
                this.findUserSubmission(data);
            },
            error(error) {
                console.error('Error fetching submissions:', error);
                this.userSubmission = null;
            },
            fetchPolicy: 'network-only',
        },
    },
    computed: {
        activityStatus() {
            if (!this.activity.start_date && !this.activity.end_date) {
                return 'current'; // Default to current if no dates
            }

            const now = new Date();
            const startDate = this.activity.start_date ? new Date(this.activity.start_date) : null;
            const endDate = this.activity.end_date ? new Date(this.activity.end_date) : null;

            // Upcoming: start_date > now
            if (startDate && startDate > now) {
                return 'upcoming';
            }

            // Past: end_date < now
            if (endDate && endDate < now) {
                return 'past';
            }

            // Current: start_date <= now AND (end_date is null OR end_date >= now)
            if (startDate && startDate <= now) {
                if (!endDate || endDate >= now) {
                    return 'current';
                }
            }

            // Default to current if start_date is null but end_date is in future
            if (!startDate && endDate && endDate >= now) {
                return 'current';
            }

            return 'current';
        },
        isLoadingSubmissions() {
            return this.$apollo.queries.mySubmissions && this.$apollo.queries.mySubmissions.loading;
        },
        isExternalActivity() {
            return this.activity && this.activity.is_external === true && this.activity.external_link;
        },
    },
    mounted() {
        // Ensure submission data is fetched
        if (this.token) {
            this.$apollo.queries.mySubmissions.refetch();
        }
    },
    watch: {
        token() {
            // Refetch when token changes
            if (this.token) {
                this.userSubmission = null;
                this.$apollo.queries.mySubmissions.refetch();
            }
        },
        '$apollo.queries.mySubmissions.loading'(loading) {
            // When loading completes, check for submission
            if (!loading && this.token) {
                this.checkForSubmission();
            }
        },
    },
    methods: {
        findUserSubmission(data) {
            if (data && data.me && !data.me.error && data.me.user && data.me.user.workReadinessSubmissions) {
                const submissions = data.me.user.workReadinessSubmissions;
                // Find submission where activity token matches
                const submission = submissions.find(
                    sub => {
                        if (!sub) return false;
                        // Check if activity exists and token matches
                        if (sub.activity && sub.activity.token === this.token) {
                            return true;
                        }
                        return false;
                    }
                );
                if (submission) {
                    this.userSubmission = submission;
                } else {
                    this.userSubmission = null;
                }
            } else {
                this.userSubmission = null;
            }
        },
        checkForSubmission() {
            // Manually check for submission from Apollo query result
            if (this.$apollo.queries.mySubmissions && this.$apollo.queries.mySubmissions.lastResult) {
                const data = this.$apollo.queries.mySubmissions.lastResult.data;
                this.findUserSubmission(data);
            }
        },
        handleFileUpload(event) {
            const file = event.target.files[0];
            if (file) {
                if (file.type !== 'application/pdf' && !file.type.includes('image/') && file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
                    alert('Only JPG, JPEG, PNG and PDF files are allowed.');
                } else if (file.size > 2 * 1024 * 1024) {
                    alert('File size must be less than 2MB.');
                } else {
                    this.form.file = file;
                }
            }
        },
        handleSubmit() {
            if (this.isExternalActivity) {
                this.confirmExternalRegistration();
            } else {
                this.submitWorkReadinessProgramActivity();
            }
        },
        async submitWorkReadinessProgramActivity() {
            // Check if activity is current
            if (this.activityStatus !== 'current') {
                this.$swal.fire('Error', 'You can only submit current activities.', 'error');
                return;
            }

            // Check if already submitted
            if (this.userSubmission) {
                this.$swal.fire('Info', 'You have already submitted this activity.', 'info');
                return;
            }

            this.isSubmitting = true;
            try {
                const { data } = await this.$apollo.mutate({
                    mutation: SUBMIT_ACTIVITY_MUTATION,
                    variables: {
                        token: this.token,
                        input: {
                            note: this.form.note,
                            document: this.form.file,
                        },
                    },
                });

                const res = data.submitWorkReadinessProgramActivity;
                if (res.error) {
                    this.$swal.fire('Error', res.message, 'error');
                } else {
                    this.$swal.fire('Success', res.message, 'success');
                    this.form.note = '';
                    this.form.file = null;
                    // Reset file input
                    if (this.$refs.fileInput) {
                        this.$refs.fileInput.value = '';
                    }
                    // Refetch submission data
                    this.$apollo.queries.mySubmissions.refetch();
                    // Redirect after a short delay
                    setTimeout(() => {
                        this.$router.replace('/my-work-readiness-program');
                    }, 1500);
                }
            } catch (error) {
                this.$swal.fire('Oops', error.message || 'Something went wrong', 'error');
            } finally {
                this.isSubmitting = false;
            }
        },
        formatDate(dateString) {
            if (!dateString) return 'N/A';
            const date = new Date(dateString);
            const options = { year: 'numeric', month: 'short', day: 'numeric' };
            return date.toLocaleDateString(undefined, options);
        },
        formatStatus(status) {
            if (!status) return 'Pending';
            return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
        },
        getStatusClass(status) {
            if (!status || status.toLowerCase() === 'pending') {
                return 'bg-yellow-100 text-yellow-700';
            } else if (status.toLowerCase() === 'approved' || status.toLowerCase() === 'completed') {
                return 'bg-green-100 text-green-700';
            } else if (status.toLowerCase() === 'rejected') {
                return 'bg-red-100 text-red-700';
            }
            return 'bg-gray-100 text-gray-700';
        },
        getFileUrl(path) {
            if (!path) return '#';
            if (path.indexOf('http') === 0) {
                return path;
            }
            return process.env.VUE_APP_PUBLIC_URL + path;
        },
        async handleOpenExternalLink() {
            if (!this.activity.external_link) return;
            try {
                await this.$apollo.mutate({
                    mutation: RECORD_EXTERNAL_LINK_CLICK,
                    variables: { token: this.token },
                });
                this.currentUserHasClickedLink = true;
                window.open(this.activity.external_link, '_blank', 'noopener,noreferrer');
            } catch (err) {
                this.$swal.fire('Error', err.message || 'Failed to record click', 'error');
            }
        },
        async confirmExternalRegistration() {
            this.isConfirming = true;
            try {
                const note = (this.form.note && this.form.note.trim()) ? this.form.note.trim() : 'Registered via external link';
                const { data } = await this.$apollo.mutate({
                    mutation: CONFIRM_EXTERNAL_REGISTRATION,
                    variables: {
                        token: this.token,
                        input: { note: note, document: this.form.file },
                    },
                });
                const res = data.confirmExternalRegistration;
                if (res.error) {
                    this.$swal.fire('Error', res.message, 'error');
                } else {
                    this.$swal.fire('Success', res.message, 'success');
                    this.form.note = '';
                    this.form.file = null;
                    if (this.$refs.fileInput) this.$refs.fileInput.value = '';
                    this.$apollo.queries.mySubmissions.refetch();
                }
            } catch (err) {
                this.$swal.fire('Error', err.message || 'Something went wrong', 'error');
            } finally {
                this.isConfirming = false;
            }
        },
    },
};
</script>